import { memo, useCallback, useMemo, useState } from "react";
import Divider from "@mui/material/Divider";
import Box from "@mui/material/Box";
import { Leaderboard, LeaderboardBase } from "types";
import LeaderboardEditableData from "./LeaderboardEditableData";
import LeaderboardEditableLabel from "./LeaderboardEditableLabel";
import LeaderboardFormikProvider from "./LeaderboardFormikProvider";
import * as Components from "./LeaderboardEditable.components";
import Portal from "components/Portal";
import LeaderboardEditableFullscreen from "./LeaderboardEditableFullscreen";
import LeaderboardEditableConfigPanel from "./LeaderboardEditableConfigPanel";
import TickerProvider from "components/Ticker/TickerProvider";
import { ThemeProvider, createTheme } from "@mui/material/styles";
import muiTheme from "components/MuiThemeProvider/muiTheme";
import { fullscreenLeaderboardTheme } from "./LeaderboardEditableFullscreen/LeaderboardEditableFullscreen.theme";
import useLocale from "providers/LocaleProvider/useLocale";
import ThemeHelpers from "helpers/ThemeHelpers";

interface LeaderboardEditableProps extends Partial<Leaderboard> {
  onSubmit(leaderboard: Omit<Leaderboard, "id">): void;
  onCancelCreate(): void;
  configPanelOpen?: boolean;
  leaderboard?: LeaderboardBase;
}

const LeaderboardEditable = memo(function LeaderboardEditable(props: LeaderboardEditableProps) {
  const [isFullscreen, setIsFullscreen] = useState(false);
  const { onSubmit, leaderboard, configPanelOpen, onCancelCreate } = props;
  const { locale } = useLocale();
  const toggleFullscreen = useCallback(() => setIsFullscreen((open) => !open), []);

  const theme = useMemo(() => {
    if (isFullscreen) {
      return createTheme(fullscreenLeaderboardTheme, ThemeHelpers.muiLocaleThemeConstructor(locale));
    }
    return createTheme(muiTheme, ThemeHelpers.muiLocaleThemeConstructor(locale));
  }, [isFullscreen, locale]);

  const LeaderboardWrapperComponent = useCallback(
    ({ children }: { children?: React.ReactNode }) => {
      if (isFullscreen) {
        return (
          <Portal>
            <LeaderboardEditableFullscreen>{children}</LeaderboardEditableFullscreen>
          </Portal>
        );
      }
      return <>{children}</>;
    },
    [isFullscreen]
  );

  return (
    <LeaderboardFormikProvider leaderboard={leaderboard} onSubmit={onSubmit}>
      <TickerProvider>
        <LeaderboardWrapperComponent>
          <Box
            sx={{
              position: "relative",
              display: "flex",
              flexDirection: "column",
              height: "100%",
            }}
          >
            <Components.CardPaper>
              <ThemeProvider theme={theme}>
                <LeaderboardEditableLabel toggleFullscreen={toggleFullscreen} isFullscreen={isFullscreen} />
                <Divider />
                <LeaderboardEditableData />
                <Divider />
              </ThemeProvider>
              <LeaderboardEditableConfigPanel
                onCancelCreate={onCancelCreate}
                isFullscreen={isFullscreen}
                configPanelOpen={configPanelOpen}
              />
            </Components.CardPaper>
          </Box>
        </LeaderboardWrapperComponent>
      </TickerProvider>
    </LeaderboardFormikProvider>
  );
});

export default LeaderboardEditable;
